import React, { useEffect, useMemo, useState } from "react";
import { Upload, message, Tooltip } from "antd";
import ImgCrop from "antd-img-crop";
import { useTranslation } from "react-i18next";
import AppIcon from "./AppIcon";
import AppModal from "./AppModal";
import { postDataAsFormData } from "api/api/axiosSetup";
import { UTILS } from "utils";

const { Dragger } = Upload;

const ACCEPTED_TYPES = {
  image: ["image/jpeg", "image/png", "image/webp"],
  video: [
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/x-msvideo",
    "video/quicktime",
    "video/x-ms-wmv",
    "video/x-flv",
    "video/x-matroska",
  ],
};

const getAcceptedMimeTypes = (accept = "image/*") => {
  if (accept === "image/*") return ACCEPTED_TYPES.image;
  if (accept === "video/*") return ACCEPTED_TYPES.video;
  return [...ACCEPTED_TYPES.image, ...ACCEPTED_TYPES.video];
};

const getFileCategory = (fileOrUrl, mimeType = "") => {
  if (mimeType) {
    if (ACCEPTED_TYPES.image.includes(mimeType)) return "image";
    if (ACCEPTED_TYPES.video.includes(mimeType)) return "video";
  }

  if (typeof fileOrUrl === "string") {
    const isVideo = fileOrUrl.match(/\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)$/i);
    return isVideo ? "video" : "image";
  }

  if (fileOrUrl?.type) {
    if (ACCEPTED_TYPES.image.includes(fileOrUrl.type)) return "image";
    if (ACCEPTED_TYPES.video.includes(fileOrUrl.type)) return "video";
  }

  return "image";
};

const normalizeValueToFileList = (value) => {
  if (!value) return [];

  const values = Array.isArray(value) ? value : [value];

  return values
    .map((item, index) => {
      const url =
        typeof item === "string" ? item : item?.url || item?.preview || "";
      if (!url) return null;

      return {
        uid: item?.uid || `initial-${index}`,
        name: item?.name || `file-${index}`,
        url,
        type: getFileCategory(url || item, item?.type || ""),
        raw: item,
        isCached: item?.isCached || false,
      };
    })
    .filter(Boolean);
};

const AppImageVideoUpload = ({
  id,
  label = "",
  width = "100%",
  height = "220px",
  maxSizeMB = 10,
  value = [],
  onChange = () => { },
  accept = "image/*",
  showGuidelines = true,
  name = "media",
  cropAspect,
  cropRotate = true,
  multiple = true,
  maxCount = 1,
  fileKey,
  isImageUpload = true,
  enableCrop = true,
  itemId = null,
  setIsDirty = () => { },
  setLoading = () => { },
  deleteRequest, // optional async function({ url, itemId, file })
}) => {
  const isAutoHeight = height === "auto";
  const { t } = useTranslation();

  const [fileList, setFileList] = useState(normalizeValueToFileList(value));
  const [previewModal, setPreviewModal] = useState({
    open: false,
    file: null,
    type: "image",
    name: "",
  });
  const [currentUploadedFile, setCurrentUploadedFile] = useState(null);

  useEffect(() => {
    setFileList(normalizeValueToFileList(value));
  }, [value]);

  const currentCount = fileList.length;
  const canUploadMore =
    typeof maxCount === "number" ? currentCount < maxCount : true;
  const isSingleOnly = currentCount === 1 && !canUploadMore;
  const shouldUseGrid =
    currentCount > 1 || (currentCount === 1 && canUploadMore);

  const acceptedMimeTypes = useMemo(
    () => getAcceptedMimeTypes(accept),
    [accept]
  );

  const emitChange = (updatedList, extra = {}) => {
    const payload = {
      fileList: updatedList,
      ...extra,
    };

    if (fileKey) {
      onChange(payload, fileKey);
    } else {
      onChange(payload);
    }
  };

  const isValidMediaFile = (file) => {
    const isValidType = acceptedMimeTypes.includes(file.type);
    if (!isValidType) {
      message.error("Unsupported file format");
      return false;
    }

    const isValidSize = file.size / 1024 / 1024 < maxSizeMB;
    if (!isValidSize) {
      message.error(`File must be smaller than ${maxSizeMB}MB`);
      return false;
    }

    if (typeof maxCount === "number" && fileList.length >= maxCount) {
      message.error(`You can upload maximum ${maxCount} file(s)`);
      return false;
    }

    return true;
  };

  const beforeUpload = (file) => {
    if (!isValidMediaFile(file)) {
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const addOrUpdateFile = (newFileItem, replaceUid = null) => {
    setFileList((prev) => {
      let updated = [...prev];

      if (replaceUid) {
        updated = updated.map((item) =>
          item.uid === replaceUid ? { ...item, ...newFileItem } : item
        );
      } else {
        const exists = updated.some((item) => item.uid === newFileItem.uid);
        updated = exists
          ? updated.map((item) =>
            item.uid === newFileItem.uid ? { ...item, ...newFileItem } : item
          )
          : [...updated, newFileItem];
      }

      if (typeof maxCount === "number") {
        updated = updated.slice(0, maxCount);
      }

      emitChange(updated, {
        file: newFileItem,
        actionType: replaceUid ? "edited" : "added",
      });

      return updated;
    });
  };

  const handleMediaUpload = async ({ file, onSuccess, onError }) => {
    try {
      setIsDirty(true);

      const isValid = isValidMediaFile(file);
      if (!isValid) {
        onError?.(new Error("Invalid file"));
        return;
      }

      setCurrentUploadedFile(file);
      setLoading(true);

      const currentUser = UTILS.getCurrentUser();

      if (typeof postDataAsFormData !== "function") {
        throw new Error("postDataAsFormData function is required");
      }

      const resp = await postDataAsFormData(
        "fms/upload-media-files",
        {
          media: file,
          itemId,
          brandId: currentUser?.brandId,
        },
        ["media"]
      );

      if (resp?.response === 200 && resp?.data) {
        const uploadedUrl =
          typeof resp.data === "string" ? resp.data : resp?.data?.url;

        const newItem = {
          uid: file.uid,
          name: file.name,
          url: uploadedUrl,
          type: getFileCategory(uploadedUrl, file.type),
          raw: file,
        };

        addOrUpdateFile(newItem);
        onSuccess?.(resp, file);
      } else {
        throw new Error(resp?.message || "Upload failed");
      }
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Failed to upload file");
      onError?.(error);
    } finally {
      setLoading(false);
      setCurrentUploadedFile(null);
    }
  };

  const handleDelete = async (file) => {
    try {
      setLoading(true);
      setIsDirty(true);

      if (deleteRequest) {
        await deleteRequest({
          url: file?.url,
          itemId,
          file,
        });
      }

      // Example if you want to use APIKit directly:
      // await APIKit.delete("fms/remove-media-file-url", {
      //   data: { url: file?.url, itemId },
      // });

      setFileList((prev) => {
        const updated = prev.filter((item) => item.uid !== file.uid);

        emitChange(updated, {
          actionType: "removed",
          removedUid: file.uid,
          file,
        });

        return updated;
      });
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Failed to delete file");
    } finally {
      setLoading(false);
    }
  };

  const renderMedia = (item, isSingleCard = false) => {
    const mediaClassName =
      isSingleCard && isAutoHeight
        ? "w-full h-auto block object-contain"
        : "w-full h-full object-contain";

    if (item.type === "image") {
      return (
        <img
          src={item.url}
          alt={item.name}
          className={mediaClassName}
        />
      );
    }

    return (
      <video
        src={item.url}
        className={mediaClassName}
        controls
      />
    );
  };

  const uploadContent = (
    <div
      className="w-full h-full border-2 border-dashed border-blue-200 rounded-xl flex flex-col items-center justify-center text-center p-6 hover:border-blue-300 transition-colors bg-white cursor-pointer"
      style={{ minHeight: height }}
    >
      <div className="mb-4 p-2 bg-gray-100 rounded-full">
        <AppIcon name="uploadDocument" />
      </div>

      <p className="text-sm font-medium text-gray-700 mb-1">
        Drag your files here or <span className="text-blue-500">Browse</span>
      </p>

      {showGuidelines && (
        <p className="text-xs text-gray-400 mt-2">
          Allowed size: {maxSizeMB}MB max
        </p>
      )}

      {typeof maxCount === "number" && (
        <p className="text-xs text-gray-400 mt-1">
          {currentCount}/{maxCount} uploaded
        </p>
      )}
    </div>
  );

  const dragContent = (
    <Dragger
      name={name}
      accept={accept}
      showUploadList={false}
      multiple={maxCount === 1 ? false : multiple}
      beforeUpload={beforeUpload}
      customRequest={handleMediaUpload}
      className="!bg-transparent border-0 outline-none"
    >
      {uploadContent}
    </Dragger>
  );

  const draggerComponent =
    isImageUpload && enableCrop && accept !== "video/*" ? (
      <ImgCrop
        modalTitle={t("Edit Image")}
        modalOk={t("Ok")}
        modalCancel={t("Cancel")}
        aspect={cropAspect}
        rotate={cropRotate}
      >
        {dragContent}
      </ImgCrop>
    ) : (
      dragContent
    );

  return (
    <div id={id} className="w-full mb-5" style={{ width }}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {currentCount === 0 ? (
        draggerComponent
      ) : (
        <div className={shouldUseGrid ? "flex flex-wrap gap-4" : "flex flex-col"}>
          {fileList.map((item) => {
            const isUploading = currentUploadedFile?.uid === item.uid;

            return (
              <Tooltip
                placement="top"
                color="cyan"
                title={
                  item?.isCached
                    ? "This file is currently stored in a temporary URL and is not yet linked to any item"
                    : null
                }
                key={item.uid}
              >
                <div
                  className={`group relative rounded-xl overflow-hidden border border-gray-200 p-1 ${item?.isCached ? "bg-yellow-100" : "bg-white"
                    }`}
                  style={
                    isSingleOnly
                      ? isAutoHeight
                        ? { height: "auto", padding: 0 }
                        : { minHeight: height, height }
                      : { height: "100px", width: "100px" }
                  }
                >
                  {renderMedia(item, isSingleOnly)}

                  {isUploading && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                      <span className="text-white text-sm font-medium">
                        Uploading...
                      </span>
                    </div>
                  )}

                  <div className="absolute inset-x-0 top-0 bottom-0 flex items-center justify-center gap-2 p-3 bg-black/35 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                    <button
                      type="button"
                      className="flex items-center justify-center"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPreviewModal({
                          open: true,
                          file: item.url,
                          type: item.type,
                          name: item.name,
                        });
                      }}
                    >
                      <AppIcon name="eye" state="inactive" />
                    </button>

                    <button
                      type="button"
                      className="flex items-center justify-center"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(item);
                      }}
                    >
                      <AppIcon name="trash" />
                    </button>
                  </div>
                </div>
              </Tooltip>
            );
          })}
        </div>
      )}

      {fileList?.length > 0 && canUploadMore && (
        <div style={{
          marginTop: "10px",
        }} className={shouldUseGrid ? "w-[100px] h-[100px]" : "w-full"}>
          <div className="w-full h-full">{draggerComponent}</div>
        </div>
      )}

      <AppModal
        open={previewModal.open}
        onClose={() =>
          setPreviewModal({
            open: false,
            file: null,
            type: "image",
            name: "",
          })
        }
        title={t("Media Preview")}
      >
        {previewModal.type === "image" ? (
          <img
            src={previewModal.file}
            alt={previewModal.name}
            className="w-full h-full object-contain"
          />
        ) : (
          <video
            src={previewModal.file}
            className="w-full h-full object-contain"
            controls
          />
        )}
      </AppModal>
    </div>
  );
};

export default AppImageVideoUpload;
